import { 
  LayoutDashboard, 
  UserCircle, 
  Newspaper, 
  Video, 
  CheckSquare, 
  PlayCircle, 
  FileEdit, 
  GraduationCap, 
  BookOpen, 
  HeartHandshake, 
  Calendar, 
  Phone, 
  LogOut,
  X,
  CalendarDays,
  ChevronDown,
  Wrench,
  Bell,
} from "lucide-react";
import { cn, toTitleCase } from "../lib/utils";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useEffect, useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { useTranslation } from "react-i18next";
import api from "../services/api";

export function Sidebar({ isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [myToolsOpen, setMyToolsOpen] = useState(false);
  const [externalLinks, setExternalLinks] = useState({
    stdl: "http://162.210.199.3/Account/?ReturnUrl=%2f",
    contactUs: "https://kingschat.online/user/lwstaffportal"
  });

  // Fetch external links from API
  useEffect(() => {
    const fetchExternalLinks = async () => {
      try {
        const response = await api.get('/settings/external-links');
        if (response.data.success && response.data.data) {
          setExternalLinks(prev => ({
            ...prev,
            ...response.data.data
          }));
        }
      } catch (error) {
        console.error('Error fetching external links:', error);
        // Keep default links if API fails
      }
    };

    fetchExternalLinks();
  }, []);

  // Check if any tool item is active to keep submenu open
  useEffect(() => {
    const toolPaths = [
      "/nda-form",
      "/staff-counselling",
      "/staff-handbook",
      "/contact-us",
      "/stdl",
    ];
    if (toolPaths.includes(location.pathname)) {
      setMyToolsOpen(true);
    }
  }, [location.pathname]);

  const menuItems = [
    { icon: LayoutDashboard, label: t('common.home'), href: "/", active: location.pathname === "/" },
    { icon: Newspaper, label: t('common.informationCenter'), href: "/information-center", active: location.pathname === "/information-center" },
    { icon: Video, label: t('common.liveProgrammes'), href: "/live-meetings", active: location.pathname === "/live-meetings" },
    { icon: CheckSquare, label: t('common.acceptMeetings'), href: "/accept-meetings", active: location.pathname === "/accept-meetings" },
    { icon: PlayCircle, label: t('common.videos'), href: "/videos", active: location.pathname === "/videos" },
    { icon: Calendar, label: t('common.staffEvents'), href: "/staff-events", active: location.pathname === "/staff-events" },
    { icon: UserCircle, label: t('common.profile'), href: "/profile", active: location.pathname === "/profile" },
    { icon: Bell, label: t('common.notifications'), href: "/notifications", active: location.pathname === "/notifications" },
    { icon: CalendarDays, label: t('common.calendar'), href: "/calendar", active: location.pathname === "/calendar" },
  ];

  const myToolsItems = [
    { icon: FileEdit, label: t('common.ndaForm'), href: "/nda-form", active: location.pathname === "/nda-form", external: false },
    { icon: HeartHandshake, label: t('common.staffCounselling'), href: "/staff-counselling", active: location.pathname === "/staff-counselling", external: false },
    { icon: BookOpen, label: t('common.staffHandbook'), href: "/staff-handbook", active: location.pathname === "/staff-handbook",  external: false },
    { icon: Phone, label: t('common.contactUs'), href: externalLinks.contactUs, active: false, external: true },
    { icon: GraduationCap, label: t('common.stdl'), href: externalLinks.stdl, active: false, external: true },
  ];

  const getUserInitials = () => {
    const name = user?.full_name || user?.name;
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return "U";
  };

  const getUserName = () => {
    return toTitleCase(user?.full_name || user?.name || "User");
  };

  const getUserEmail = () => {
    return user?.email || user?.emailAddress || user?.portal_id || "user@company.com";
  };

  const getUserDepartment = () => {
    // Handle case where department is an object
    if (user?.department && typeof user.department === 'object') {
      return user.department.name || "Staff";
    }
    // Handle case where department is a string
    if (user?.department && typeof user.department === 'string') {
      return user.department;
    }
    // Fallback to department_name or default
    return user?.department_name || "Staff";
  };

  const getUserImage = () => {
    const imageUrl = user?.picture_path || user?.picturePath || user?.image || user?.photo || user?.avatar;
    
    // Return null if imageUrl is null, undefined, or empty string
    // This will trigger the AvatarFallback to show
    if (!imageUrl || imageUrl === 'null' || imageUrl === 'undefined') {
      return undefined; // Return undefined so AvatarFallback shows
    }
    
    // Ensure the URL is a valid string
    return String(imageUrl).trim() || undefined;
  };

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 flex-col border-r border-border bg-card/90 backdrop-blur-md z-40 shadow-lg">
        <nav className="flex-1 space-y-2 p-4 overflow-y-auto">
          {menuItems.map((item, index) => (
            <Link
              key={index}
              to={item.href}
              className={cn(
                "flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden",
                item.active
                  ? "bg-gradient-to-r from-[#1E74FD] to-blue-600 text-white shadow-lg shadow-[#1E74FD]/30 scale-[1.02]"
                  : "text-foreground hover:bg-gradient-to-r hover:from-[#1E74FD]/10 hover:to-transparent hover:scale-[1.01]"
              )}
            >
              {item.active && (
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent" />
              )}
              <div className="flex items-center gap-3">
                <div className={cn(
                  "relative flex items-center justify-center transition-all duration-300",
                  item.active ? "text-white" : "text-muted-foreground group-hover:text-[#1E74FD]"
                )}>
                  <item.icon className={cn(
                    "size-5 transition-all duration-300 relative z-10",
                    item.active ? "scale-110" : "group-hover:scale-110"
                  )} />
                  {item.active && (
                    <div className="absolute inset-0 bg-white/20 rounded-lg blur-sm -z-0" />
                  )}
                </div>
                <span className={cn(
                  "font-medium transition-colors duration-300",
                  item.active ? "text-white font-semibold" : "text-foreground"
                )}>{item.label}</span>
              </div>
              {item.badge && (
                <Badge variant="destructive" className="text-xs px-1.5 py-0">
                  {item.badge}
                </Badge>
              )}
            </Link>
          ))}

          {/* My Tools Submenu */}
          <Collapsible open={myToolsOpen} onOpenChange={setMyToolsOpen} className="w-full">
            <CollapsibleTrigger className={cn(
              "w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden",
              myToolsOpen || myToolsItems.some(item => item.active)
                ? "bg-gradient-to-r from-[#1E74FD]/10 to-transparent"
                : "text-foreground hover:bg-gradient-to-r hover:from-[#1E74FD]/10 hover:to-transparent"
            )}>
              <div className="flex items-center gap-3">
                <div className={cn(
                  "relative flex items-center justify-center transition-all duration-300",
                  myToolsOpen ? "text-[#1E74FD]" : "text-muted-foreground group-hover:text-[#1E74FD]"
                )}>
                  <Wrench className="size-5 transition-all duration-300" />
                </div>
                <span className="font-medium transition-colors duration-300">{t('common.resources')}</span>
              </div>
              <ChevronDown className={cn(
                "size-4 transition-transform duration-300",
                myToolsOpen && "transform rotate-180"
              )} />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-1 pl-4">
              {myToolsItems.map((item, index) => {
                const MenuItem = item.external ? 'a' : Link;
                const menuItemProps = item.external 
                  ? { href: item.href, target: "_blank", rel: "noopener noreferrer" }
                  : { to: item.href };
                
                return (
                  <MenuItem
                    key={index}
                    {...menuItemProps}
                    className={cn(
                      "flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg transition-all duration-300 group relative overflow-hidden",
                      item.active
                        ? "bg-gradient-to-r from-[#1E74FD] to-blue-600 text-white shadow-md shadow-[#1E74FD]/20"
                        : "text-foreground hover:bg-gradient-to-r hover:from-[#1E74FD]/10 hover:to-transparent"
                    )}
                  >
                    {item.active && (
                      <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent" />
                    )}
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "relative flex items-center justify-center transition-all duration-300",
                        item.active ? "text-white" : "text-muted-foreground group-hover:text-[#1E74FD]"
                      )}>
                        <item.icon className={cn(
                          "size-4 transition-all duration-300 relative z-10",
                          item.active ? "scale-110" : "group-hover:scale-110"
                        )} />
                      </div>
                      <span className={cn(
                        "text-sm font-medium transition-colors duration-300",
                        item.active ? "text-white font-semibold" : "text-foreground"
                      )}>{item.label}</span>
                    </div>
                    {item.badge && (
                      <Badge variant="destructive" className="text-xs px-1.5 py-0">
                        {item.badge}
                      </Badge>
                    )}
                  </MenuItem>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        </nav>
        
        {/* User Profile Section */}
        <div className="p-4 border-t border-border bg-gradient-to-t from-[#1E74FD]/5 to-transparent">
          <Link 
            to="/profile" 
            className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gradient-to-r hover:from-[#1E74FD]/10 hover:to-transparent transition-all duration-300 group mb-3"
          >
            <Avatar className="size-12 ring-2 ring-[#1E74FD]/20 group-hover:ring-[#1E74FD]/40 transition-all">
              {getUserImage() && (
                <AvatarImage 
                  src={getUserImage()} 
                  alt={getUserName()} 
                />
              )}
              <AvatarFallback className="bg-gradient-to-br from-[#1E74FD] to-blue-600 text-white font-semibold text-sm">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate group-hover:text-[#1E74FD] transition-colors">
                {getUserName()}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {getUserDepartment()}
              </p>
            </div>
          </Link>
          <Button
            variant="destructive"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            className="w-full flex items-center justify-center gap-2"
          >
            <LogOut className="size-4" />
            {t('common.logout')}
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      <div
        className={cn(
          "lg:hidden fixed inset-0 bg-black/60 z-[55] backdrop-blur-sm transition-opacity duration-300",
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />
      <aside className={cn(
        "lg:hidden fixed left-0 top-0 h-full w-[280px] max-w-[85vw] flex flex-col border-r border-border bg-card backdrop-blur-md z-[60] shadow-2xl transition-transform duration-300 ease-out",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}>
            {/* Mobile Header */}
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-border bg-gradient-to-r from-[#1E74FD]/5 to-transparent">
              <h2 className="text-lg font-semibold text-foreground">Menu</h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg active:bg-accent transition-colors text-muted-foreground active:text-foreground touch-manipulation"
                aria-label="Close menu"
              >
                <X className="size-5" />
              </button>
            </div>
            <nav className="flex-1 min-h-0 space-y-2 p-4 overflow-y-auto">
              {menuItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.href}
                  onClick={onClose}
                  className={cn(
                    "flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden touch-manipulation",
                    item.active
                      ? "bg-gradient-to-r from-[#1E74FD] to-blue-600 text-white shadow-lg shadow-[#1E74FD]/30"
                      : "text-foreground active:bg-gradient-to-r active:from-[#1E74FD]/10 active:to-transparent"
                  )}
                >
                  {item.active && (
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent" />
                  )}
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "relative flex items-center justify-center transition-all duration-300",
                      item.active ? "text-white" : "text-muted-foreground group-hover:text-[#1E74FD]"
                    )}>
                      <item.icon className={cn(
                        "size-5 transition-all duration-300 relative z-10",
                        item.active ? "scale-110" : "group-hover:scale-110"
                      )} />
                      {item.active && (
                        <div className="absolute inset-0 bg-white/20 rounded-lg blur-sm -z-0" />
                      )}
                    </div>
                    <span className={cn(
                      "font-medium transition-colors duration-300",
                      item.active ? "text-white font-semibold" : "text-foreground"
                    )}>{item.label}</span>
                  </div>
                  {item.badge && (
                    <Badge variant="destructive" className="text-xs px-1.5 py-0">
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              ))}

              {/* My Tools Submenu - Mobile */}
              <Collapsible open={myToolsOpen} onOpenChange={setMyToolsOpen} className="w-full">
                <CollapsibleTrigger className={cn(
                  "w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden touch-manipulation",
                  myToolsOpen || myToolsItems.some(item => item.active)
                    ? "bg-gradient-to-r from-[#1E74FD]/10 to-transparent"
                    : "text-foreground active:bg-gradient-to-r active:from-[#1E74FD]/10 active:to-transparent"
                )}>
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "relative flex items-center justify-center transition-all duration-300",
                      myToolsOpen ? "text-[#1E74FD]" : "text-muted-foreground group-hover:text-[#1E74FD]"
                    )}>
                      <Wrench className="size-5 transition-all duration-300" />
                    </div>
                    <span className="font-medium transition-colors duration-300">{t('common.resources')}</span>
                  </div>
                  <ChevronDown className={cn(
                    "size-4 transition-transform duration-300",
                    myToolsOpen && "transform rotate-180"
                  )} />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 space-y-1 pl-4">
                  {myToolsItems.map((item, index) => {
                    const MenuItem = item.external ? 'a' : Link;
                    const menuItemProps = item.external 
                      ? { href: item.href, target: "_blank", rel: "noopener noreferrer", onClick: onClose }
                      : { to: item.href, onClick: onClose };
                    
                    return (
                      <MenuItem
                        key={index}
                        {...menuItemProps}
                        className={cn(
                          "flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg transition-all duration-300 group relative overflow-hidden touch-manipulation",
                          item.active
                            ? "bg-gradient-to-r from-[#1E74FD] to-blue-600 text-white shadow-md shadow-[#1E74FD]/20"
                            : "text-foreground active:bg-gradient-to-r active:from-[#1E74FD]/10 active:to-transparent"
                        )}
                      >
                        {item.active && (
                          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent" />
                        )}
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "relative flex items-center justify-center transition-all duration-300",
                            item.active ? "text-white" : "text-muted-foreground group-hover:text-[#1E74FD]"
                          )}>
                            <item.icon className={cn(
                              "size-4 transition-all duration-300 relative z-10",
                              item.active ? "scale-110" : "group-hover:scale-110"
                            )} />
                          </div>
                          <span className={cn(
                            "text-sm font-medium transition-colors duration-300",
                            item.active ? "text-white font-semibold" : "text-foreground"
                          )}>{item.label}</span>
                        </div>
                        {item.badge && (
                          <Badge variant="destructive" className="text-xs px-1.5 py-0">
                            {item.badge}
                          </Badge>
                        )}
                      </MenuItem>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            </nav>
            
            {/* User Profile Section */}
            <div className="flex-shrink-0 p-4 pb-20 border-t border-border bg-gradient-to-t from-[#1E74FD]/5 to-transparent">
              <Link 
                to="/profile" 
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-3 rounded-xl active:bg-gradient-to-r active:from-[#1E74FD]/10 active:to-transparent transition-all duration-300 group mb-3"
              >
                <Avatar className="size-12 ring-2 ring-[#1E74FD]/20 group-hover:ring-[#1E74FD]/40 transition-all">
                  {getUserImage() && (
                    <AvatarImage 
                      src={getUserImage()} 
                      alt={getUserName()} 
                    />
                  )}
                  <AvatarFallback className="bg-gradient-to-br from-[#1E74FD] to-blue-600 text-white font-semibold text-sm">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate group-hover:text-[#1E74FD] transition-colors">
                    {getUserName()}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {getUserDepartment()}
                  </p>
                </div>
              </Link>
              <Button
                variant="destructive"
                onClick={async () => {
                  await logout();
                  onClose();
                  navigate('/login');
                }}
                className="w-full flex items-center justify-center gap-2 touch-manipulation"
              >
                <LogOut className="size-4" />
                {t('common.logout')}
              </Button>
            </div>
          </aside>
    </>
  );
}
